import { api } from './client'

export interface GpsState {
    device_id: string
    imei: string
    latitude: number
    longitude: number
    speed: number
    direction: number
    address: string
    motion_status: number
    update_time: string
    today_distance: number
    fuel_level?: number | null
    battery_percent?: number | null
    has_camera: boolean
}

export interface SensorDTO {
    sensor_name: string
    sensor_type: string
    value: string
    original_value: number
}

export type VehicleGpsError =
    'no_gps' | 'disabled' | 'not_found' | 'upstream' | null

export interface VehicleGpsDetail {
    vehicle_id: string
    plate_number: string
    name: string
    status: string
    configured: boolean
    disabled: boolean
    gps_error?: VehicleGpsError
    gps: GpsState | null
    sensors: SensorDTO[]
    has_camera: boolean
    use_new_media_api: boolean
    driving_continuously_time: number
    today_driving_time: number
    week_driving_time: number
    number_of_stops: number
    number_of_speed_violation: number
}

export interface GpsResolveResult {
    vehicle_id: string
    device_id: string
    imei: string
    has_camera: boolean
    use_new_media_api: boolean
}

export interface GpsPoint {
    latitude: number
    longitude: number
    speed: number
    direction: number
    time: string
}

export interface PlaybackSegment {
    type: 'route' | 'stop'
    total_distance: number
    start_time: string
    end_time: string
    start_address: string
    end_address: string
    status_duration: string
    points?: GpsPoint[]
}

export interface CameraPhoto {
    id: string
    device_id: string
    camera_id: number
    camera_name: string
    time_stamp_utc: number
    latitude: number
    longitude: number
    address: string
    image_url: string
}

export interface CameraLatestResult {
    vehicle_id: string
    plate_number: string
    name: string
    configured: boolean
    disabled: boolean
    has_camera: boolean
    gps_error?: VehicleGpsError
    photos: CameraPhoto[]
}

export interface CameraJanusLiveStream {
    device_imei: string
    index: number
    node_id: string
    stream_id: string
    address: string
}

export interface CameraLegacyLiveStream {
    id: string
    name: string
    description: string
    device_id: string
    camera_id: number
    url: string
}

export interface CameraLiveStartResult {
    mode: 'janus' | 'legacy'
    streams: CameraJanusLiveStream[]
    legacy_streams: CameraLegacyLiveStream[]
}

// An offline camera is a device state the operator already sees on screen,
// not a failure. A device unknown to VnetGPS media (wrong IMEI) stays an
// api_error because an admin has to fix it.
const CAMERA_LIVE_UNAVAILABLE = [
    { status: 409, code: 'CAMERA_OFFLINE', outcome: 'camera_live_unavailable' },
]

export const vehicleGpsApi = {
    detail: (vehicleId: string) =>
        api.get<VehicleGpsDetail>(`/vehicles/${vehicleId}/gps`),
    resolve: (vehicleId: string) =>
        api.post<GpsResolveResult>(`/vehicles/${vehicleId}/gps/resolve`),
    playback: (vehicleId: string, start: string, end: string) =>
        api.get<PlaybackSegment[]>(`/vehicles/${vehicleId}/gps/playback`, {
            params: { start, end },
        }),
    latestCamera: (vehicleId: string) =>
        api.get<CameraLatestResult>(`/vehicles/${vehicleId}/cameras/latest`),
    startCameraLive: (vehicleId: string, cameraIds: number[]) =>
        api.post<CameraLiveStartResult>(
            `/vehicles/${vehicleId}/cameras/live/start`,
            { camera_ids: cameraIds },
            { expected: CAMERA_LIVE_UNAVAILABLE },
        ),
    keepCameraLiveAlive: (vehicleId: string, streamId: string) =>
        api.post<{ ok: boolean }>(
            `/vehicles/${vehicleId}/cameras/live/keep-alive`,
            { stream_id: streamId },
        ),
    stopCameraLive: (
        vehicleId: string,
        data: { index: number; stream_id: string; node_id: string },
    ) =>
        api.post<{ ok: boolean }>(
            `/vehicles/${vehicleId}/cameras/live/stop`,
            data,
        ),
}
